import React from 'react';
import cn from 'classnames';
import ReactDOM from 'react-dom';

import {StandardProps} from '..';
import Backdrop from '../Backdrop';
import Paper, {PaperProps} from '../Paper';
import {Direction, Fade, Slide} from '../Transition';
import useOnClickOutside from '../../hooks/useOnClickOutside';

type CloseReason = 'escapeKeyDown' | 'backdropClick';
type MaxWidth = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | false;

function getDialogWidthClass(maxWidth?: MaxWidth) {
  switch (maxWidth) {
    case 'xxs':
      return 'dialog__paper--width-xxs';
    case 'xs':
      return 'dialog__paper--width-xs';
    case 'sm':
      return 'dialog__paper--width-sm';
    case 'md':
      return 'dialog__paper--width-md';
    case 'lg':
      return 'dialog__paper--width-lg';
    default:
      return undefined;
  }
}

type PaperComponentProps = React.ComponentPropsWithRef<typeof Paper>;
type PaperComponentType = React.ForwardRefExoticComponent<PaperComponentProps>;

export interface DialogProps extends StandardProps {
  open: boolean;
  onClose?(e: MouseEvent | TouchEvent | KeyboardEvent, reason: CloseReason): void;
  PaperComponent?: PaperComponentType;
  PaperProps?: PaperProps;
  fullScreen?: boolean;
  fullWidth?: boolean;
  mobileMode?: boolean;
  onDialogTransitionEnd?(): void;
  maxWidth?: MaxWidth;
  scroll?: 'body' | 'paper';
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  containerClassName?: string;
  animationDirection?: Direction;
}

export const Dialog: React.FC<DialogProps> = ({
  children,
  className,
  open,
  onClose,
  PaperComponent = Paper,
  PaperProps = {},
  fullScreen,
  fullWidth,
  mobileMode,
  maxWidth = 'sm',
  scroll = 'paper',
  onDialogTransitionEnd,
  closeOnEsc = true,
  closeOnBackdropClick,
  containerClassName,
  animationDirection,
  ...restProps
}) => {
  const paperRef = React.useRef<HTMLDivElement>(null);

  const handleClose = React.useCallback((e: MouseEvent | TouchEvent| KeyboardEvent, reason: CloseReason) => {
    e.stopPropagation();
    onClose?.(e, reason);
  }, [onClose]);

  const handleBackdropClick = (e: MouseEvent | TouchEvent) => {
      handleClose(e, 'backdropClick');
  };

  useOnClickOutside(paperRef, handleBackdropClick);

  React.useEffect(() => {
    let listener;

    if (closeOnEsc) {
      listener = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleClose(e, 'escapeKeyDown');
        }
      };
      document.addEventListener('keydown', listener);
    }

    return () => {
      if (closeOnEsc) {
        document.removeEventListener('keydown', listener);
      }
    };
  }, [handleClose, closeOnEsc]);

  const {className: paperClassName, ...restPaperProps} = PaperProps;

  return ReactDOM.createPortal(
    <div className={cn('dialog', {'scroll-body': scroll === 'body'}, className)} {...restProps}>
      <Fade in={open}>
        <Backdrop open style={{zIndex: -1}} />
      </Fade>

      <Slide in={open} direction={animationDirection} onExited={onDialogTransitionEnd}>
        <div
          className={cn(
            'dialog__container',
            containerClassName,
            {'dialog__container--mobile-mode pt-5': mobileMode},
          )}
        >
          <PaperComponent
            ref={paperRef}
            className={cn('dialog__paper',
              getDialogWidthClass(maxWidth),
              paperClassName,
              {
                'dialog__paper--full-screen': fullScreen,
                'dialog__paper--full-width': fullWidth,
                'h-100': mobileMode,
              },
            )}
            {...restPaperProps}
          >
            {children}
          </PaperComponent>
        </div>
      </Slide>
    </div>,
    document.body,
  );
};

Dialog.displayName = 'Dialog';

export default Dialog;
